import React from 'react';
import { eachDayOfInterval, endOfWeek, format, isSameDay, startOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Booking, Settings } from '../../types/booking';
import { dayIsOpen, toMinutes, toTime } from '../../utils/availability';
import { dayStats, dayWindow, placeBookings, statusAccent } from '../../utils/agenda';
import { euro } from '../../utils/format';
type Props = {
  anchor: Date;
  bookings: Booking[];
  settings: Settings;
  selected: string;
  onSelectDate: (iso: string) => void;
  onOpenBooking: (booking: Booking) => void;
};
const PX_PER_MIN = 1.1;
export function AgendaWeek({
  anchor,
  bookings,
  settings,
  selected,
  onSelectDate,
  onOpenBooking
}: Props) {
  const days = eachDayOfInterval({
    start: startOfWeek(anchor, {
      weekStartsOn: 1
    }),
    end: endOfWeek(anchor, {
      weekStartsOn: 1
    })
  });
  const isoDays = days.map(d => format(d, 'yyyy-MM-dd'));
  const {
    start,
    end
  } = dayWindow(isoDays, settings);
  const height = (end - start) * PX_PER_MIN;
  const hourMarks: number[] = [];
  for (let m = Math.ceil(start / 60) * 60; m <= end; m += 60) hourMarks.push(m);
  return <div className="overflow-hidden rounded-2xl border border-edge bg-carbon" data-id="element-167" data-mp="3rsgvoggdv4ee">
      <div className="overflow-x-auto" data-id="element-168" data-mp="3ds6398zfdhr4">
      <div className="min-w-[760px]" data-id="element-169" data-mp="3dt7i7ojkaoe4">
      <div className="flex border-b border-edge" data-id="element-170" data-mp="3iqpunowsflqv">
        <span className="w-14 shrink-0 border-r border-edge" aria-hidden="true" data-id="element-171" data-mp="3iris1z9uwywc" />
        {days.map((day, i) => {
            const iso = isoDays[i];
            const stats = dayStats(bookings, iso);
            const today = isSameDay(day, new Date());
            return <button key={iso} type="button" onClick={() => onSelectDate(iso)} className={`flex-1 border-r border-edge px-2 py-3 text-left last:border-r-0 transition-colors duration-200 ease-smooth hover:bg-panel ${iso === selected ? 'bg-panel' : ''}`} data-id="element-172" data-mp="lmkvqvvmlk4" data-mpi={i}>
              <span className="block text-[11px] uppercase tracking-[0.14em] text-dim" data-id="element-173" data-mp="y2l9kzakreuz">
                {format(day, 'EEEEEE', {
                  locale: nl
                })}
              </span>
              <span className={`mt-1 inline-flex h-7 min-w-7 items-center justify-center rounded-full px-1.5 font-display text-sm font-extrabold ${today ? 'bg-ember text-void' : 'text-chalk'}`} data-id="element-174" data-mp="12yfhricq4292">
                {format(day, 'd')}
              </span>
              <span className="mt-1 block truncate text-[10px] text-dim" data-id="element-175" data-mp="oz7hem4tqo3a">
                {settings.blockedDates.includes(iso) ? 'Geblokkeerd' : !dayIsOpen(iso, settings) ? 'Gesloten' : stats.count > 0 ? `${stats.count}× · ${euro(stats.revenue)}` : 'Vrij'}
              </span>
            </button>;
          })}
      </div>

      <div className="flex" data-id="element-176" data-mp="18ae1xr3gz3n">
        <div className="relative w-14 shrink-0 border-r border-edge" style={{
            height
          }} data-id="element-177" data-mp="193iut6htej5">
          {hourMarks.map(mark => <span key={mark} className="absolute right-2 -translate-y-1/2 text-[10px] text-dim" style={{
              top: (mark - start) * PX_PER_MIN
            }} data-id="element-178" data-mp="2cv44rgeybcn4">
              {toTime(mark)}
            </span>)}
        </div>

        {isoDays.map(iso => {
            const open = dayIsOpen(iso, settings);
            const blocked = settings.blockedDates.includes(iso);
            const hours = settings.hours[new Date(`${iso}T00:00:00`).getDay()];
            const placed = placeBookings(bookings.filter(b => b.date === iso && b.status !== 'cancelled'));
            return <div key={iso} className="relative flex-1 border-r border-edge last:border-r-0" style={{
              height
            }} data-id="element-179" data-mp="27wastsfbbtp2">
              {hourMarks.map(mark => <span key={mark} className="absolute inset-x-0 border-t border-edge/60" style={{
                top: (mark - start) * PX_PER_MIN
              }} aria-hidden="true" data-id="element-180" data-mp="1ag5ahjvnvnvt" />)}

              {open && hours.open && hours.close && !blocked && <span className="absolute inset-x-0 bg-void/50" style={{
                top: 0,
                height: (toMinutes(hours.open) - start) * PX_PER_MIN
              }} aria-hidden="true" data-id="element-181" data-mp="3d1hhla6wjhlu" />}
              {open && hours.open && hours.close && !blocked && <span className="absolute inset-x-0 bg-void/50" style={{
                top: (toMinutes(hours.close) - start) * PX_PER_MIN,
                bottom: 0
              }} aria-hidden="true" data-id="element-182" data-mp="3vta9106bourg" />}
              {(!open || blocked) && <span className={`absolute inset-0 ${blocked ? 'bg-ember/[0.07]' : 'bg-void/60'}`} aria-hidden="true" data-id="element-183" data-mp="2be8analdwx3a" />}

              {placed.map(({
                booking,
                start: s,
                lane,
                lanes
              }) => <button key={booking.id} type="button" onClick={() => onOpenBooking(booking)} className={`absolute overflow-hidden rounded-lg border px-1.5 py-1 text-left text-[11px] leading-tight transition-colors duration-200 ease-smooth hover:border-ember ${statusAccent(booking)}`} style={{
                top: (s - start) * PX_PER_MIN,
                height: Math.max(booking.durationMinutes * PX_PER_MIN - 3, 22),
                left: `calc(${lane / lanes * 100}% + 2px)`,
                width: `calc(${100 / lanes}% - 4px)`
              }} data-id="element-184" data-mp="25bdfgoqokyk8">
                  <span className="block font-semibold" data-id="element-185" data-mp="32xkhprziietf">{booking.time}</span>
                  <span className="block truncate" data-id="element-186" data-mp="37y89wgzh91p8">{booking.customer.name}</span>
                </button>)}
            </div>;
          })}
      </div>
      </div>
      </div>
    </div>;
}